import React, { memo, FunctionComponent, Fragment } from 'react';
import styled from 'styled-components';
import { Form, Field } from 'react-final-form';
import { useIntl, FormattedMessage } from 'react-intl';
import { Box, InputField, SelectField, Label, Text } from 'mdlkit';
import {
  emailComposed,
  nameComposed,
  password,
  zipCodeComposed,
  validateDob,
  phoneNumberComposed,
} from '../../utils/register/validations';
import {
  normalizeNumberOnlyWithLength,
  normalizeNumbersOnly,
  normalizeZipCode,
} from '../../utils/register/normalize';
import {
  StyledForm,
  StyledFlex,
  FormSubmitLoadingButton,
} from '../shared/Styles';
import Gender from './Gender';
import PasswordField from './PasswordField';
import V2RegisterBreadcrumb from './V2RegisterBreadcrumb';
import PatientAffiliationList from './ProfileForm/PatientAffiliationList';
import { EmailField, LegalLink, DOBGroup } from './PatientForm';
import {
  PatientSubmitError,
  PatientAffiliation,
} from '../../interfaces/register';
import { V2PatientProfile } from '../../interfaces/patient';
import { required } from '../../utils/shared/validations';
import { ErrorMessage } from '../../utils/shared/interfaces';
import { MONTHS } from '../../constants/months';

const StyledGender = styled(Gender)`
  margin-top: ${({ theme }) => theme.space.xs}px !important;
`;

interface RecordErrors {
  gender?: string;
  dobDay?: string;
  dobMonth?: string;
  dobYear?: string;
}

interface Props {
  onSubmit: (values: V2PatientProfile) => Promise<void | PatientSubmitError>;
  termsLink?: string;
  privacyLink?: string;
  affiliations: PatientAffiliation[] | null;
  is270: boolean;
  isDtc: boolean;
  affiliationId?: number;
  initialValues?: Partial<V2PatientProfile>;
  lockIdentity?: boolean;
  coverageFlow?: boolean;
  heading?: string;
  onValidationError?: (errors: Record<string, ErrorMessage>) => void;
}

const V2PatientForm: FunctionComponent<Props> = ({
  onSubmit,
  termsLink,
  privacyLink,
  affiliations,
  is270,
  isDtc,
  affiliationId,
  initialValues,
  lockIdentity = false,
  coverageFlow = false,
  heading,
  onValidationError,
}: Props) => {
  const { formatMessage: f } = useIntl();
  const intlMonths = MONTHS.map((item) => ({
    name: f({ id: `month.${item.name.toLowerCase()}` }),
    value: item.value,
  }));
  // Coverage-check flow also carries the verified affiliation, not just DTC.
  const affiliationIdValue = isDtc || coverageFlow ? affiliationId : undefined;

  return (
    <Fragment>
      <V2RegisterBreadcrumb
        heading={
          typeof heading === 'undefined'
            ? f({ id: 'register.createAccountAffiliationHeader' })
            : heading
        }
        hideBreadcrumb={!is270}
      />

      <Text small pt="sm">
        <FormattedMessage id="register.dependantsAnnouncement" />
      </Text>

      <Form
        initialValues={{
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          gender: '',
          dobMonth: '',
          dobDay: '',
          dobYear: '',
          phone: '',
          zip: '',
          acceptTerms: true,
          // Prefilled identity overrides empty defaults (never email/password/phone);
          // affiliationId is set last so the carried value wins.
          ...initialValues,
          affiliationId: affiliationIdValue,
        }}
        onSubmit={onSubmit}
        validate={(values: V2PatientProfile) => {
          const errors: RecordErrors = {};

          if (typeof values.gender === 'undefined') {
            errors.gender = f({ id: 'validations.gender' });
          }
          return {
            ...errors,
            ...validateDob(values),
          };
        }}
        render={({
          handleSubmit,
          submitting,
          values,
          errors,
          hasValidationErrors,
          hasSubmitErrors,
          dirtySinceLastSubmit,
        }) => (
          <StyledForm
            onSubmit={(e) => {
              // A submit blocked by client-side validation never reaches the real onSubmit, so
              // report the attempt here before handing off to react-final-form.
              if (hasValidationErrors && onValidationError) {
                onValidationError(errors as Record<string, ErrorMessage>);
              }
              handleSubmit(e);
            }}
            spellCheck={false}
          >
            <Field
              id="email"
              label={f({ id: 'register.email' })}
              name="email"
              validate={emailComposed}
              parse={(value) => value && value.trim()}
              component={EmailField}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />

            <Field
              id="password"
              label={f({ id: 'register.password' })}
              name="password"
              validate={password}
              values={values}
              component={PasswordField}
            />

            <StyledFlex>
              <Field
                id="firstName"
                label={f({ id: 'register.firstName' })}
                name="firstName"
                validate={nameComposed}
                component={InputField}
                autoCorrect="off"
                spellCheck={false}
                maxLength={35}
                disabled={lockIdentity}
              />
              <Field
                id="lastName"
                label={f({ id: 'register.lastName' })}
                name="lastName"
                validate={nameComposed}
                component={InputField}
                autoCorrect="off"
                spellCheck={false}
                maxLength={35}
                disabled={lockIdentity}
              />
            </StyledFlex>

            <Box width={1}>
              <Label as="label" htmlFor="gender">
                <FormattedMessage id="register.gender" />
              </Label>
              <StyledGender pt={0} disabled={lockIdentity} />
            </Box>

            <Box as="fieldset">
              <Label as="legend">
                <FormattedMessage id="register.dateOfBirth" />
              </Label>
              <DOBGroup mt="xs">
                <Box>
                  <Field
                    id="dobMonth"
                    label={f({ id: 'month' })}
                    name="dobMonth"
                    validate={required}
                    component={SelectField}
                    blank
                    options={intlMonths}
                    disabled={lockIdentity}
                  />
                </Box>
                <Box>
                  <Field
                    id="dobDay"
                    label={f({ id: 'day' })}
                    name="dobDay"
                    validate={required}
                    component={InputField}
                    inputMode="numeric"
                    maxLength={2}
                    parse={normalizeNumbersOnly}
                    disabled={lockIdentity}
                  />
                </Box>
                <Box>
                  <Field
                    id="dobYear"
                    label={f({ id: 'year' })}
                    name="dobYear"
                    validate={required}
                    component={InputField}
                    inputMode="numeric"
                    maxLength={4}
                    parse={normalizeNumbersOnly}
                    disabled={lockIdentity}
                  />
                </Box>
              </DOBGroup>
            </Box>

            <Field
              id="phone"
              label={f({ id: 'register.phone' })}
              name="phone"
              inputMode="numeric"
              validate={phoneNumberComposed}
              component={InputField}
              parse={(value: string) =>
                normalizeNumberOnlyWithLength(value, 10)
              }
              aria-label="phone number"
            />

            <Field
              id="zip"
              label={f({ id: 'register.homeZipCode' })}
              name="zip"
              inputMode="numeric"
              validate={zipCodeComposed}
              component={InputField}
              parse={normalizeZipCode}
              aria-label="home zip code"
              disabled={lockIdentity}
            />

            {affiliations && (
              <PatientAffiliationList affiliations={affiliations} />
            )}

            <Box mt="sm">
              <Field
                id="acceptTerms"
                name="acceptTerms"
                validate={required}
                component="input"
                hidden
              />
              <LegalLink termsLink={termsLink} privacyLink={privacyLink} />
            </Box>
            <FormSubmitLoadingButton
              id="createAccountBtn"
              type="submit"
              size="large"
              disabled={
                submitting || (hasSubmitErrors && !dirtySinceLastSubmit)
              }
              isLoading={submitting}
            >
              <FormattedMessage id="register.createAccount" />
            </FormSubmitLoadingButton>
          </StyledForm>
        )}
      />
    </Fragment>
  );
};

export default memo(V2PatientForm);
